'use client';

import { useSignIn } from '@clerk/nextjs';
import { getLogger } from '@lib/observability/logging/log.client';
import { useMutation, useQueryClient } from '@lib/query/client';

const { logError } = getLogger('lib/auth/hooks/use-exchange-sign-in-ticket');

export interface UseExchangeSignInTicketResult {
  exchangeTicket: (ticket: string) => Promise<void>;
  isExchanging: boolean;
}

/**
 * Exchanges a Clerk sign-in ticket for an active session via
 * `create({ strategy: 'ticket' })` and `finalize`, then wipes
 * the TanStack Query cache for the auth transition.
 */
export function useExchangeSignInTicket(): UseExchangeSignInTicketResult {
  const { signIn } = useSignIn();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (ticket: string) => {
      if (!signIn) {
        throw new Error('Sign in is not available');
      }

      const { error: ticketError } = await signIn.create({
        strategy: 'ticket',
        ticket,
      });
      if (ticketError || signIn.status !== 'complete') {
        throw new Error(
          ticketError?.message ??
            (signIn.status !== 'complete'
              ? 'Sign in incomplete'
              : 'Sign in failed')
        );
      }

      const { error: finalizeError } = await signIn.finalize();
      if (finalizeError) {
        throw new Error(finalizeError.message || 'Session activation failed');
      }
    },
    retry: false,
    onSuccess: () => {
      queryClient.clear();
    },
    onError: (err: unknown) => {
      // Log error so we can investigate ticket exchange failures
      logError('Ticket exchange failed', err as Error);
    },
  });

  return {
    exchangeTicket: (ticket: string) => mutation.mutateAsync(ticket),
    isExchanging: mutation.isPending,
  };
}
